import * as React from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import { top } from '../utils/customPropStyles';
import Badge from './Badge';
import Icon from './Icon';
import Translate, { translate } from './Translate';
import FilterSearch from './FilterSearch';
import FilterValueComponent from './FilterValue';
import ClickLayer from './ClickLayer';
import { FilterItemProps, FilterValue } from '../models/components/Filter';
import { action, computed, observable } from 'mobx';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import {
    List,
    CellMeasurer,
    CellMeasurerCache,
    AutoSizer,
} from 'react-virtualized';
import InlineDisclaimers from './InlineDisclaimers';
import { SuperscriptScope } from '../stores';
import { Popper } from '../utils/Popper';
import { createPortal } from 'react-dom';
import { Modifier, OptionsGeneric } from '@popperjs/core';

export interface RowItem {
    index: number;
    key: any;
    style: any;
    parent: any;
}

const FilterDropdownLabelWrapper = styled<any, any>(
    'div',
    'filterDropdownLabelWrapper',
    'filterItemDropdown'
)`
    ${propStyles};
`;

const FilterDropdownWrapper = styled<any, any>(
    'div',
    'filterDropdownWrapper',
    'filterItemDropdown'
)`
    ${propStyles};
`;

FilterDropdownWrapper.defaultProps = {
    flex: ['0 0 100%', '0 0 33%', '0 0 20%'],
    maxWidth: ['100%', '33%', '20%'],
};

const FilterDropdownRow = styled<any, any>(
    'div',
    'filterDropdownRow',
    'filterItemDropdown'
)`
    ${propStyles};
`;

const FilterListAutosizerWrapper = styled<any, any>(
    'div',
    'filterListAutosizerWrapper',
    'filterItemDropdown'
)`
    ${propStyles};
`;

FilterListAutosizerWrapper.defaultProps = {
    height: '300px',
};

const FilterDropdownInnerWrapper = styled<any, any>(
    'div',
    'filterDropdownInnerWrapper',
    'filterItemDropdown'
)`
    ${propStyles};
`;

FilterDropdownInnerWrapper.defaultProps = {
    position: 'relative',
    marginX: 'lg',
    marginY: 'sm',
};

const FilterDropdownHeader = styled<any, any>(
    'label',
    'filterDropdownHeader',
    'filterItemDropdown'
)`
    ${propStyles};
`;

FilterDropdownHeader.defaultProps = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 'sm',
    fontFamily: 'heading',
    fontWeight: 'medium',
    color: 'body',
    margin: '0 0 0.5em 0',
};

const FilterDropdownButton = styled<any, any>(
    'button',
    'filterDropdownButton',
    'filterItemDropdown'
)`
    ${propStyles};
    outline: none;
    cursor: pointer;
`;

FilterDropdownButton.defaultProps = {
    display: 'flex',
    flex: '0 0 auto',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: 'sm',
    fontSize: 'sm',
    fontFamily: 'body',
    fontWeight: 'normal',
    color: 'gray500',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    hover: {
        borderColor: 'primary',
    },
    selected: {
        color: 'body',
        borderColor: 'primary',
    },
};

const FilterDropdownText = styled<any, any>(
    'div',
    'filterDropdownText',
    'filterItemDropdown'
)`
    ${propStyles};
    white-space: nowrap;
`;

FilterDropdownText.defaultProps = {
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: 'inherit',
};

const FilterDropdown = styled<any, any>(
    'div',
    'filterDropdown',
    'filterItemDropdown'
)`
    ${propStyles};
    ${top};
`;

FilterDropdown.defaultProps = {
    position: 'absolute',
    top: 'calc(100% + 1px)',
    left: '0',
    right: '0',
    zIndex: 'dropdown',
    padding: 'md',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    boxShadow: 'sm',
    overflow: 'hidden',
};

const ClearIndividualFiltersButton = styled<any, any>(
    'button',
    'clearIndividualFiltersButton',
    'filterItemDropdown'
)`
    ${propStyles};
`;

ClearIndividualFiltersButton.defaultProps = {
    display: 'flex',
    fontFamily: 'body',
    fontWeight: 'normal',
    fontSize: 'sm',
    color: 'gray500',
    bgColor: 'transparent',
    borderStyle: 'none',
    cursor: 'pointer',
    outline: 'none',
    selected: {
        color: 'body',
    },
};

const rightAlignStyleProps = {
    order: 1,
    margin: '0 0 0 auto',
    zIndex: 1010,
};

const SelectedFilterInfo = styled<any, any>(
    'div',
    'selectedFilterInfo',
    'filterItemDropdown'
)`
    ${propStyles};
`;

SelectedFilterInfo.defaultProps = {
    display: 'flex',
};

const FilterList = styled<any, any>('ol', 'filterList', 'filterItemDropdown')`
    ${propStyles};
    list-style: none;
    -ms-overflow-style: -ms-autohiding-scrollbar;
`;

FilterList.defaultProps = {
    maxHeight: '300px',
    overflowX: 'hidden',
    overflowY: 'auto',
};

const Divider = styled<any, any>('hr', 'divider', 'filterItemDropdown')`
    ${propStyles};
`;

Divider.defaultProps = {
    borderWidth: '1px',
    borderColor: 'gray500',
    borderStyle: 'dotted none none none',
};

@inject('dataStore')
@inject('translationStore')
@inject('filterStore')
@inject('accessibilityStore')
@observer
class FilterItemDropdown extends Popper<FilterItemProps> {
    @computed
    get popperEnabled(): boolean {
        return this.props.expanded;
    }

    @computed
    get showClear() {
        const { search } = this.props;
        return search.length > 0;
    }

    @computed
    get filteredValues() {
        return this.props && this.props.filteredValues;
    }

    @computed
    get mustVirtualize() {
        const { filter } = this.props;
        return filter.dropdownOptions && filter.dropdownOptions.virtualize;
    }

    @computed
    get autosizeFn() {
        const overscanRowCount = 10;
        return ({ width, height }: { width: any; height: any }) => {
            return (
                <List
                    updated={this.updated++}
                    width={width}
                    height={height}
                    rowHeight={this.cache.rowHeight}
                    rowRenderer={this.renderRow}
                    rowCount={this.filteredValues.length}
                    overscanRowCount={overscanRowCount}
                />
            );
        };
    }

    @computed
    get filterDropdownHiddenStyle(): React.CSSProperties {
        if (!this.showDropdown) {
            return {
                visibility: 'hidden',
                pointerEvents: 'none',
            };
        }
        return {};
    }

    @observable updated = 1; // Setting this forces the autosizer to re-render when the component props change
    @observable cache: CellMeasurerCache;
    @observable.ref showDropdown: boolean = false;

    popperOptions: Partial<OptionsGeneric<Partial<Modifier<any, any>>>> = this
        .props?.popperOptions
        ? this.props.popperOptions
        : {
              placement: 'bottom-start',
              modifiers: [
                  {
                      name: 'offset',
                      options: {
                          offset: [0, 1],
                      },
                  },
                  {
                      name: 'flip',
                      enabled: false,
                  },
                  {
                      ...this.sameWidth,
                  },
              ],
          };

    constructor(props) {
        super(props);
        this.cache = new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: props.defaultRowHeight || 100,
        });
    }

    @action
    setFilterDropdownElement = (element: HTMLElement) => {
        this.setPopupNodeRef(element);
        // Show the visually hidden Dropdown after Popper initialization, this ensures correct positioning
        if (element) {
            this.showDropdown = true;
            return;
        }
        this.showDropdown = false;
    };

    renderFilterValue = (
        filterValue: FilterValue,
        state: { active: boolean; checked: boolean },
        themeOverride?
    ) => {
        const { filterStore, filter, toggleExpanded, theme } = this.props;
        const {
            property,
            filterSearchKey,
            ui,
            allowInactiveSelection = true,
        } = filter;
        const { active, checked } = state;
        const disabled = allowInactiveSelection === false && active === false;
        const onChange = e => {
            filterStore.toggleFilter(
                property,
                filterValue.value,
                filterSearchKey,
                checked,
                filter
            );
            if (ui === 'dropdown') {
                toggleExpanded(e);
            }
        };

        return (
            <FilterValueComponent
                key={`${filterValue.label}`}
                label={filterValue.label}
                ui={ui}
                value={filterValue.value}
                checked={checked}
                disabled={disabled}
                onChange={onChange}
                active={active}
                filterProperty={property}
                {...(themeOverride || theme.FilterDropdownFilterListItem)}
            />
        );
    };

    renderRow = ({ index, key, style, parent }: RowItem) => {
        const { theme } = this.props;
        const filteredValue = this.filteredValues[index];
        const active = filteredValue.isActive;
        return (
            <CellMeasurer
                key={key}
                cache={this.cache}
                parent={parent}
                columnIndex={0}
                rowIndex={index}
            >
                <FilterDropdownRow key={key} style={style}>
                    {this.renderFilterValue(
                        filteredValue,
                        {
                            active,
                            checked: false,
                        },
                        theme.FilterDropdownFilterListItem
                    )}
                </FilterDropdownRow>
            </CellMeasurer>
        );
    };

    render() {
        const {
            theme,
            dataStore,
            translationStore,
            filterStore,
            accessibilityStore,
            filter,
            // withFilter HoC props
            expanded,
            toggleExpanded,
            selectedCount,
            total,
            search,
            clearSearch,
            selectedValues,
            filteredValues,
            filterSearch,
            enableSearch,
            filterDropdownText,
            isRequired = false,
            disclaimers,
        } = this.props;
        const isDropdown = filter.ui === 'dropdown';
        const dropdownId = filter.label.toLowerCase().replace(/\s/g, '-');

        const dropdownElement = dataStore.shadowRoot.getElementById(
            'fundcenter-dropdown'
        );

        return (
            <FilterDropdownWrapper {...theme.FilterDropdownWrapper}>
                <FilterDropdownInnerWrapper
                    ref={this.setAnchorNodeRef}
                    {...theme.FilterDropdownInnerWrapper}
                >
                    <FilterDropdownHeader
                        htmlFor={dropdownId}
                        {...theme.FilterDropdownHeader}
                    >
                        <FilterDropdownLabelWrapper
                            {...theme.FilterDropdownLabelWrapper}
                        >
                            <Translate>{filter.label}</Translate>
                            <InlineDisclaimers
                                conditionalDisclaimers={disclaimers}
                                superscriptScope={SuperscriptScope.module}
                                forceSuperScripts={false}
                            />
                        </FilterDropdownLabelWrapper>
                        {selectedCount > 0 && (
                            <SelectedFilterInfo>
                                {filterStore.clearIndividualFilters.enabled && (
                                    <ClearIndividualFiltersButton
                                        aria-label={accessibilityStore.buttonAccessibilityValue(
                                            'ClearIndividualFiltersButton'
                                        )}
                                        {...(filterStore.clearIndividualFilters
                                            .align === 'right' &&
                                            rightAlignStyleProps)}
                                        {...theme.ClearIndividualFiltersButton}
                                        onClick={
                                            filterStore.filtersActive
                                                ? e => {
                                                      filterStore.clearFiltersOfType(
                                                          filter.property,
                                                          filter.filterSearchKey
                                                      );
                                                      if (expanded) {
                                                          toggleExpanded(e);
                                                      }
                                                  }
                                                : () => undefined
                                        }
                                        data-metric-id={`filterItem-clear-${camelCase(
                                            filter.property
                                        )}`}
                                    >
                                        <Icon
                                            height="1.4em"
                                            width="1.4em"
                                            margin="0 0.25em 0 0.25em"
                                            fileName="ClearFiltersIcon"
                                            {...theme.ClearFiltersIcon}
                                        />
                                    </ClearIndividualFiltersButton>
                                )}
                                {!isDropdown && (
                                    <Badge
                                        fontSize="0.75em"
                                        data-selected={selectedCount > 0}
                                        selected={{
                                            bgColor: 'primary',
                                        }}
                                        {...theme.FilterDropdownTotalBadge}
                                    >
                                        {selectedCount}/{total}
                                    </Badge>
                                )}
                            </SelectedFilterInfo>
                        )}
                    </FilterDropdownHeader>
                    <FilterDropdownButton
                        aria-label={accessibilityStore.buttonAccessibilityValue(
                            'FilterDropdownButton'
                        )}
                        id={dropdownId}
                        onClick={toggleExpanded}
                        title={filterDropdownText}
                        data-selected={expanded || selectedCount > 0}
                        data-metric-id={`filterItemDropdown-button-${camelCase(
                            filter.property
                        )}`}
                        aria-expanded={expanded}
                        aria-controls={`${dropdownId}-expanded-content`}
                        {...theme.FilterDropdownButton}
                        {...(isRequired
                            ? theme.FilterDropdownButtonRequired
                            : {})}
                    >
                        <FilterDropdownText {...theme.FilterDropdownText}>
                            {filterDropdownText}
                        </FilterDropdownText>
                        <Icon
                            fileName={
                                expanded
                                    ? 'FilterDropdownIconExpanded'
                                    : 'FilterDropdownIcon'
                            }
                            {...theme.FilterDropdownIcon}
                        />
                    </FilterDropdownButton>
                    {expanded && (
                        <div id={`${dropdownId}-expanded-content`}>
                            <ClickLayer
                                data-metric-id={'filterItemDropdown-clickLayer'}
                                onClick={toggleExpanded}
                            />
                            {createPortal(
                                <FilterDropdown
                                    ref={this.setFilterDropdownElement}
                                    {...theme.FilterDropdown}
                                    style={{
                                        ...this.filterDropdownHiddenStyle,
                                    }}
                                >
                                    {enableSearch && (
                                        <FilterSearch
                                            type="text"
                                            placeholder={`${translate(
                                                translationStore,
                                                'Search'
                                            )} ${translate(
                                                translationStore,
                                                filter.label
                                            )}`}
                                            value={search}
                                            onChange={e =>
                                                filterSearch(e.target.value)
                                            }
                                            onClear={clearSearch}
                                            showClear={this.showClear}
                                            {...theme.FilterDropdownSearch}
                                        />
                                    )}
                                    <FilterList
                                        {...theme.FilterDropdownSelectedFilterList}
                                    >
                                        {selectedValues.map(value =>
                                            this.renderFilterValue(
                                                value,
                                                {
                                                    active: true,
                                                    checked: true,
                                                },
                                                theme.FilterDropdownSelectedFilterListItem
                                            )
                                        )}
                                    </FilterList>
                                    {selectedValues.length > 0 &&
                                        filteredValues.length > 0 && (
                                            <Divider
                                                {...theme.FilterDropdownFilterListDivider}
                                            />
                                        )}
                                    <FilterList
                                        {...theme.FilterDropdownFilterList}
                                    >
                                        {this.mustVirtualize && (
                                            <FilterListAutosizerWrapper>
                                                <AutoSizer
                                                    children={this.autosizeFn}
                                                />
                                            </FilterListAutosizerWrapper>
                                        )}
                                        {!this.mustVirtualize &&
                                            filteredValues.reduce(
                                                (
                                                    acc,
                                                    filteredValue,
                                                    valueIndex
                                                ) => {
                                                    const active =
                                                        filteredValue.isActive;
                                                    if (
                                                        !isDropdown ||
                                                        !filter.dropdownOptions ||
                                                        !filter.dropdownOptions
                                                            .hideInactive ||
                                                        active
                                                    ) {
                                                        acc.push(
                                                            this.renderFilterValue(
                                                                filteredValue,
                                                                {
                                                                    active,
                                                                    checked: false,
                                                                },
                                                                theme.FilterDropdownFilterListItem
                                                            )
                                                        );
                                                    }
                                                    return acc;
                                                },
                                                []
                                            )}
                                    </FilterList>
                                </FilterDropdown>,
                                dropdownElement
                            )}
                        </div>
                    )}
                </FilterDropdownInnerWrapper>
            </FilterDropdownWrapper>
        );
    }
}

export default withTheme(FilterItemDropdown);



// WEBPACK FOOTER //
// ./src/Components/FilterItemDropdown.tsx